// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { type Dispatch, type FC, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { DateInput } from '@shared/ui-kit/components';
import { NumericFormat } from 'react-number-format';
import { useAppSelector } from '@advisor-app/store/store';
import { selectWorkflowAsset } from '@advisor-app/store/slices/workflow';
import { useTableRow } from '../hooks/use-table-row';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { InputPercentage } from '@advisor-app/components';

export interface TableRowProps {
  readonly id: number;

  readonly portfolio: PortfolioLoaded;

  readonly initialValue: Required<PortfolioLoaded>;

  readonly setInitialValue: Dispatch<SetStateAction<Required<PortfolioLoaded>>>;

  readonly setData: Dispatch<SetStateAction<PortfolioLoaded[]>>;

  readonly updateMyData: (rowIndex: number, columnId: string, value: any) => void;

  readonly invalidPositions?: string[];

  readonly autocomplete?: boolean;

  readonly mode: 'weight' | 'outstanding_balance';

  readonly totalWeight: number;
}

export const TableRow: FC<TableRowProps> = props => {
  const { t } = useTranslation();
  const {
    isInvalid,
    onAddRow,
    onBlur,
    onChange,
    onRemoveRow,
    onValueChange,
    workflowType,
  } = useTableRow(props);

  const active =
    ((props.portfolio?.outstanding_balance ?? 0) > 0 ||
      props.portfolio.weight > 0) &&
    !!props.portfolio.ticker;
  const formattedTicker = props.portfolio.ticker.toUpperCase();

  const assetType = useAppSelector(selectWorkflowAsset);

  return (
    <div
      aria-invalid={isInvalid}
      data-testid={`table-positions-row-${props.id}`}
      className={classNames(
        'relative grid grid-cols-[repeat(4,minmax(120px,1fr))_20px] gap-x-2.5 p-[5px] transition-all last-of-type:rounded-bl-lg last-of-type:rounded-br-lg aria-[invalid=true]:bg-negative/20 aria-[invalid=false]:focus-within:bg-primary/20',
        {
          'rounded-tl-lg rounded-tr-lg': props.id === 0,
          'rounded-lg': props.id === -1,
        },
      )}
    >
      {workflowType !== 'rebalance' && props.autocomplete ? (
        <AutocompleteInput
          onChangeValue={onChange}
          value={formattedTicker}
          name="ticker"
          error={!isInvalid}
          onBlur={onBlur('ticker', formattedTicker)}
          noMargin
          data-testid="search-ticker-input"
          assetType={assetType ? `${assetType}s` : undefined}
        />
      ) : (
        <input
          className="input-common !money-display"
          type="text"
          placeholder={t('common.ticker', { ns: 'common' })}
          value={formattedTicker}
          name="ticker"
          onChange={onChange}
          onBlur={onBlur('ticker', formattedTicker)}
          data-testid="ticker-input"
        />
      )}

      {props.mode === 'outstanding_balance' ? (
        <NumericFormat
          value={props.portfolio.outstanding_balance}
          thousandSeparator={true}
          min={1}
          decimalScale={2}
          onValueChange={({ value }) => {
            onValueChange(value, 'outstanding_balance');
          }}
          onBlur={onBlur(
            'outstanding_balance',
            props.portfolio.outstanding_balance!,
          )}
          className="input-common !money-display"
          placeholder={t('portfolio.currency-symbol', { ns: 'components' })}
          name="outstanding_balance"
          required
          data-testid="outstanding-balance-input"
        />
      ) : (
        <InputPercentage
          value={props.portfolio.weight}
          onChange={value => onValueChange(value ?? 0, 'weight')}
          onBlur={onBlur('weight', props.portfolio.weight)}
          data-testid="weight-input"
          totalWeight={props.totalWeight}
          precision={1}
          step={0.5}
          max={100}
        />
      )}

      <NumericFormat
        value={props.portfolio.unrealized_capital_gains}
        thousandSeparator={true}
        min={1}
        decimalScale={2}
        onValueChange={({ value }) => {
          onValueChange(value, 'unrealized_capital_gains');
        }}
        onBlur={onBlur(
          'unrealized_capital_gains',
          props.portfolio.unrealized_capital_gains,
        )}
        className="input-common !money-display"
        placeholder={t('portfolio.currency-symbol', { ns: 'components' })}
        name="unrealized_capital_gains"
        required
        data-testid="unrealized-capital-gains-input"
      />

      <DateInput
        className="input-common"
        value={props.portfolio.date}
        name="date"
        onChange={value => onValueChange(value, 'date')}
        onBlur={onBlur('date', props.portfolio.date!)}
        data-testid="portfolio-date-input"
      />

      <div className="grid w-5 items-center justify-center">
        {props.id === -1 ? (
          <IconButton
            variant="icon"
            color="success"
            icon="Add"
            size="lg"
            onClick={onAddRow}
            tabIndex={-1}
            data-testid="btn-add-row"
            disabled={!active}
          />
        ) : (
          <IconButton
            variant="icon"
            icon="Eliminate"
            size="lg"
            color="danger"
            onClick={onRemoveRow}
            tabIndex={-1}
            data-testid={`btn-delete-row-${props.id}`}
          />
        )}
      </div>
    </div>
  );
};
